import * as stylex from '@stylexjs/stylex';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactElement } from 'react';

import {
  Breadcrumb,
  Breadcrumbs,
  Heading,
  Paragraph,
  View,
} from '@/libs/serenity-ui';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';

export const Route = createFileRoute('/_main/_details/campaigns/archive/guide/')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }
  },
  staticData: {
    details: {
      heading: 'Archive campaigns',
      slots: {
        topBarComponent: TopBarComponent,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Campaigns — Archive campaigns',
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  return (
    <article {...stylex.props(articleStyles.base)}>
      <View style={introSectionStyles.base}>
        <View style={introListStyles.wrapper}>
          <Paragraph color="secondary" size={16} style={introListStyles.lead}>
            Campaign archive lets you move irrelevant campaigns to a dedicated archive view. This way, you can keep
            focus on campaigns that matter now while preserving historical campaign data.
          </Paragraph>
          <Paragraph color="secondary" size={16} style={introListStyles.lead}>
            Campaigns in the following statuses can be archived:
          </Paragraph>
          <ul {...stylex.props(introListStyles.base)}>
            <li {...stylex.props(introListStyles.nestedItem)}>Draft</li>
            <li {...stylex.props(introListStyles.nestedItem)}>Completed</li>
            <li {...stylex.props(introListStyles.nestedItem)}>Ended</li>
          </ul>
        </View>
      </View>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} weight="medium">Archive campaigns</Heading>
        <ol {...stylex.props(orderedListStyles.base)}>
          <li {...stylex.props(orderedListStyles.item)}>
            In the dashboard, go to <strong {...stylex.props(textStyles.primary)}>Campaigns</strong>.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Campaigns</strong> page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Click the name of the campaign you want to archive.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Edit</strong> page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            In the top-right corner, open the context menu and select{' '}
            <strong {...stylex.props(textStyles.primary)}>Archive</strong>.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Campaigns</strong> page and displays the
            success message. The campaign is no longer in the list.
          </li>
        </ol>
      </section>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} weight="medium">Access the archive</Heading>
        <ol {...stylex.props(orderedListStyles.base)}>
          <li {...stylex.props(orderedListStyles.item)}>
            In the dashboard, go to <strong {...stylex.props(textStyles.primary)}>Campaigns</strong>.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Campaigns</strong> page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            In the top-right corner, open the context menu and select{' '}
            <strong {...stylex.props(textStyles.primary)}>Archive</strong>.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Archive</strong> page. Here, you can see all
            archived campaigns.
          </li>
        </ol>
      </section>
    </article>
  );
}

function TopBarComponent(): ReactElement {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink to="/campaigns/archive/guide">Archive campaigns</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

const articleStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 88,
    justifySelf: 'center',
    maxWidth: 728,
    minWidth: 0,
    width: '100%',
  },
});

const introSectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },
});

const introListStyles = stylex.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  lead: {
    marginBottom: 18,
  },
  base: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    margin: 0,
    paddingLeft: 20,
  },
  nestedItem: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
  },
});

const sectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});

const orderedListStyles = stylex.create({
  base: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    margin: 0,
    paddingLeft: 20,
  },
  item: {
    marginBottom: 0,
  },
});

const textStyles = stylex.create({
  primary: {
    color: $.textPrimary,
    fontWeight: 600,
  },
});
